import Header from '../../components/Header';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  Stack,
  Paper,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ResponsiveBar } from '@nivo/bar';
import moment from 'moment/moment';
import { FilterContext } from '../../contexts/FilterContext';
import RoiTestsTable from '../../components/vnr/RoiTestsTable';
import RoiTestsPerNewsletterTable from '../../components/vnr/RoiTestsPerNewsletterTable';
import KpiCard from '../../components/vnr/KpiCard';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { dateToSqlDate } from '../../helpers/dateFuncs';

interface Network {
  name: string;
}

interface Theme {
  name: string;
}

interface Regime {
  gevPct?: number;
  invPct?: number;
  activeFrom: string;
  lastSeen: string;
  leads: number;
  isCurrent: boolean;
  partialRegime?: boolean;
}

interface BucketCatalog {
  bucket: string;
  name: string;
  color: string;
  hasSplit: boolean;
  regimes: Regime[];
}

interface RegimeCatalog {
  mostRecentChangeBucket: string | null;
  lookbackDays: number;
  buckets: BucketCatalog[];
}

interface DetailRow {
  test_group: string;
  leads: number;
  total_cost: number;
  revenue_30d: number;
  revenue_total: number;
  anfos_30d: number;
}

interface ArmMetrics {
  leads: number;
  cost: number;
  rev30: number;
  roi30: number | null;
  conversion: number | null;
  costPerLead: number | null;
}

// The LDBT-AC cross-sell outage leaked existing GEV→INV customers into the %-split
// arms over this window, inflating the INVESTOR arm's apparent ROI. Passive note only.
const CONTAMINATION = { start: '2026-05-18', end: '2026-06-23' };
const TINY_SAMPLE_LEADS = 20;

const GEV_COLOR = '#2196f3';
const INV_COLOR = '#4caf50';

const aggregateArm = (rows: DetailRow[]): ArmMetrics => {
  const leads = rows.reduce((acc, r) => acc + (r.leads || 0), 0);
  const cost = rows.reduce((acc, r) => acc + (r.total_cost || 0), 0);
  const rev30 = rows.reduce((acc, r) => acc + (r.revenue_30d || 0), 0);
  const anfos = rows.reduce((acc, r) => acc + (r.anfos_30d || 0), 0);
  return {
    leads,
    cost,
    rev30,
    roi30: cost > 0 ? (rev30 / cost) * 100 : null,
    conversion: leads > 0 ? (anfos / leads) * 100 : null,
    costPerLead: leads > 0 ? cost / leads : null,
  };
};

const fmtMetric = (value: number | null | undefined, digits: number, unit: string): string =>
  value == null ? '–' : formatNumber(value, digits) + unit;

const fmtDateDE = (iso: string): string => (iso ? moment(iso).format('DD.MM.YYYY') : '');

const regimeLabel = (regime: Regime): string => {
  const split = `GEV ${regime.gevPct}% / INV ${regime.invPct}%`;
  const since = regime.activeFrom ? ` — seit ${fmtDateDE(regime.activeFrom)}` : '';
  return split + since + (regime.isCurrent ? ' (neuestes mit Daten)' : '');
};

const RoiTestsPage: FC = () => {
  const { filterState, setFilterState } = useContext(FilterContext)!;
  const [networks, setNetworks] = useState<Network[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [originalDates, setOriginalDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  } | null>(null);

  const [catalog, setCatalog] = useState<RegimeCatalog | null>(null);
  const [detailRows, setDetailRows] = useState<DetailRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedBucket, setSelectedBucket] = useState<string>('');
  const [selectedRegimeIdx, setSelectedRegimeIdx] = useState<number>(0);

  // Page-specific date default (last 30 days), restored on unmount.
  useEffect(() => {
    setOriginalDates({
      startDate: filterState.startDate,
      endDate: filterState.endDate,
    });
    setFilterState((prev) => ({
      ...prev,
      startDate: moment().subtract(30, 'days').toDate(),
      endDate: moment().toDate(),
    }));
  }, []);

  useEffect(() => {
    return () => {
      if (originalDates) {
        setFilterState((prev) => ({
          ...prev,
          startDate: originalDates.startDate,
          endDate: originalDates.endDate,
        }));
      }
    };
  }, [originalDates, setFilterState]);

  useEffect(() => {
    fetch('/api/vnr/networks')
      .then((response) => response.json())
      .then((data) => {
        setNetworks(data);
        const allNetworkNames = data.map((n: Network) => n.name);
        setFilterState((prev) => ({ ...prev, network: allNetworkNames }));
      })
      .catch((error) => console.error('Error fetching networks:', error));
  }, []);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const params = new URLSearchParams();
        const selectedNetworks = Array.isArray(filterState.network)
          ? filterState.network
          : filterState.network
            ? [filterState.network]
            : [];
        if (selectedNetworks.length > 0) {
          selectedNetworks.forEach((network) => params.append('network', network));
        }
        if (filterState.startDate && filterState.endDate) {
          params.append('start_date', filterState.startDate.toISOString().split('T')[0]);
          params.append('end_date', filterState.endDate.toISOString().split('T')[0]);
        }
        const response = await fetch(`/api/vnr/themes?${params.toString()}`);
        const data = await response.json();
        setThemes(data);

        const availableThemeNames = data.map((theme: Theme) => theme.name);
        const currentSelectedThemes = Array.isArray(filterState.theme)
          ? filterState.theme
          : filterState.theme
            ? [filterState.theme]
            : [];
        const validSelectedThemes = currentSelectedThemes.filter((themeName) =>
          availableThemeNames.includes(themeName),
        );
        if (validSelectedThemes.length !== currentSelectedThemes.length) {
          setFilterState((prev) => ({ ...prev, theme: validSelectedThemes }));
        }
      } catch (error) {
        console.error('Error fetching themes:', error);
      }
    };
    fetchThemes();
  }, [filterState.network, filterState.startDate, filterState.endDate]);

  // Fetch the regime catalog (test/regime selector + "current" marker) and the
  // detailed per-cohort metrics that drive the GEV-vs-INV cards + chart.
  useEffect(() => {
    if (!filterState.startDate || !filterState.endDate) return;
    let cancelled = false;

    const networkList = Array.isArray(filterState.network)
      ? filterState.network
      : filterState.network
        ? [filterState.network]
        : [];
    const themeList = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const filters = {
      ...(networkList.length > 0 && { network: networkList }),
      ...(themeList.length > 0 && { theme: themeList }),
    };

    setLoading(true);
    (async () => {
      try {
        const [catRes, detail] = await Promise.all([
          fetchData<any>('/api/vnr/roi-tests/regimes', { ...filters }, false),
          fetchData<any>(
            '/api/vnr/roi-tests',
            {
              start_date: dateToSqlDate(filterState.startDate as Date),
              end_date: dateToSqlDate(filterState.endDate as Date),
              aggregation: 'detailed',
              ...filters,
            },
            true,
          ),
        ]);
        if (cancelled) return;
        setCatalog((catRes?.data as RegimeCatalog) ?? null);
        setDetailRows(
          (detail as Record<string, any>[]).map((r) => ({
            test_group: r.test_group,
            leads: parseInt(r.leads) || 0,
            total_cost: parseFloat(r.total_cost) || 0,
            revenue_30d: parseFloat(r.revenue_30d) || 0,
            revenue_total: parseFloat(r.revenue_total) || 0,
            anfos_30d: parseInt(r.anfos_30d) || 0,
          })),
        );
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading roi-tests catalog/detail:', error);
          setCatalog(null);
          setDetailRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filterState.startDate, filterState.endDate, filterState.network, filterState.theme]);

  // Default the selected test to the most-recently-changed bucket when the catalog
  // (re)loads. Guarded so a user's manual selection survives refetches.
  useEffect(() => {
    if (!catalog || catalog.buckets.length === 0) return;
    const exists = catalog.buckets.some((b) => b.bucket === selectedBucket);
    if (!exists) {
      setSelectedBucket(catalog.mostRecentChangeBucket || catalog.buckets[0].bucket);
    }
  }, [catalog]);

  // Reset the regime sub-selector to the current regime whenever the test changes.
  useEffect(() => {
    if (!catalog) return;
    const bucketObj = catalog.buckets.find((b) => b.bucket === selectedBucket);
    if (!bucketObj) return;
    const currentIdx = bucketObj.regimes.findIndex((r) => r.isCurrent);
    setSelectedRegimeIdx(currentIdx >= 0 ? currentIdx : 0);
  }, [selectedBucket, catalog]);

  const contaminated = useMemo(() => {
    if (!filterState.startDate || !filterState.endDate) return false;
    const start = moment(filterState.startDate).format('YYYY-MM-DD');
    const end = moment(filterState.endDate).format('YYYY-MM-DD');
    return start <= CONTAMINATION.end && end >= CONTAMINATION.start;
  }, [filterState.startDate, filterState.endDate]);

  const selectedBucketObj = catalog?.buckets.find((b) => b.bucket === selectedBucket) || null;
  const selectedRegime =
    selectedBucketObj?.regimes[selectedRegimeIdx] || selectedBucketObj?.regimes[0] || null;

  const armMetrics = useMemo<
    { gev: ArmMetrics; inv: ArmMetrics } | { ac: ArmMetrics } | null
  >(() => {
    if (!selectedBucketObj || !selectedRegime) return null;
    if (!selectedBucketObj.hasSplit) {
      const acRows = detailRows.filter(
        (r) => r.test_group === 'ALREADY_CUSTOMER' || r.test_group.startsWith('AC'),
      );
      return { ac: aggregateArm(acRows) };
    }
    const gevLabel = `${selectedBucketObj.bucket}-${selectedRegime.gevPct} (GEV)`;
    const invLabel = `${selectedBucketObj.bucket}-${selectedRegime.invPct} (INV)`;
    return {
      gev: aggregateArm(detailRows.filter((r) => r.test_group === gevLabel)),
      inv: aggregateArm(detailRows.filter((r) => r.test_group === invLabel)),
    };
  }, [selectedBucketObj, selectedRegime, detailRows]);

  const getSelectedNetworks = (): string[] =>
    Array.isArray(filterState.network)
      ? filterState.network
      : filterState.network
        ? [filterState.network]
        : [];
  const getSelectedThemes = (): string[] =>
    Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];

  const handleNetworkChange = (event: any) => {
    const value = event.target.value;
    setFilterState((prev) => ({
      ...prev,
      network: typeof value === 'string' ? value.split(',') : value,
    }));
  };
  const handleThemeChange = (event: any) => {
    const value = event.target.value;
    setFilterState((prev) => ({
      ...prev,
      theme: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  // --- Derived render values (plain, post-hooks) ---
  const gev = armMetrics && 'gev' in armMetrics ? armMetrics.gev : null;
  const inv = armMetrics && 'inv' in armMetrics ? armMetrics.inv : null;
  const ac = armMetrics && 'ac' in armMetrics ? armMetrics.ac : null;

  // Scope the detail tables to the SELECTED regime's cohorts (not the whole bucket),
  // so their totals match the cards exactly.
  const selectedTestGroups: string[] | undefined = !selectedBucketObj
    ? undefined
    : selectedBucketObj.hasSplit && selectedRegime
      ? [
          `${selectedBucketObj.bucket}-${selectedRegime.gevPct} (GEV)`,
          `${selectedBucketObj.bucket}-${selectedRegime.invPct} (INV)`,
        ]
      : ['AC (GEV)', 'AC (INV)'];
  const tableScopeLabel =
    selectedBucketObj && selectedBucketObj.hasSplit && selectedRegime
      ? `${selectedBucketObj.name} — GEV ${selectedRegime.gevPct}% / INV ${selectedRegime.invPct}%`
      : selectedBucketObj?.name || '';

  const tinySample = !!selectedRegime && selectedRegime.leads < TINY_SAMPLE_LEADS;
  const noWindowData = !!selectedBucketObj?.hasSplit && (gev?.leads || 0) + (inv?.leads || 0) === 0;

  let winner: 'gev' | 'inv' | null = null;
  if (selectedBucketObj?.hasSplit && !selectedRegime?.partialRegime && !tinySample) {
    const roiG = gev?.roi30;
    const roiI = inv?.roi30;
    if (roiG != null || roiI != null) {
      if (roiI == null) winner = 'gev';
      else if (roiG == null) winner = 'inv';
      else winner = roiG >= roiI ? 'gev' : 'inv';
    }
  }

  const chartData =
    gev && inv
      ? [
          {
            metric: 'ROI 30T',
            GEVESTOR: Number((gev.roi30 ?? 0).toFixed(1)),
            INVESTOR: Number((inv.roi30 ?? 0).toFixed(1)),
          },
          {
            metric: 'Conversion',
            GEVESTOR: Number((gev.conversion ?? 0).toFixed(2)),
            INVESTOR: Number((inv.conversion ?? 0).toFixed(2)),
          },
        ]
      : [];

  const renderArmCard = (brand: 'GEV' | 'INV', m: ArmMetrics | null, pct?: number) => {
    const isWinner = (brand === 'GEV' && winner === 'gev') || (brand === 'INV' && winner === 'inv');
    const color = brand === 'GEV' ? GEV_COLOR : INV_COLOR;
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          flex: 1,
          minWidth: 300,
          backgroundColor: '#ffffff',
          border: isWinner ? '2px solid #2e7d32' : '1px solid #e0e0e0',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
          <Typography variant="subtitle1" fontWeight={700}>
            {brand === 'GEV' ? 'GEVESTOR' : 'INVESTOR'}
          </Typography>
          {pct != null && <Chip size="small" label={`${pct}%`} />}
          {isWinner && <Chip size="small" color="success" label="🏆 Gewinner (ROI 30T)" />}
        </Stack>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <KpiCard label="ROI 30 Tage" value={fmtMetric(m?.roi30, 1, ' %')} />
          <KpiCard label="Conversion" value={fmtMetric(m?.conversion, 2, ' %')} />
          <KpiCard label="Leads" value={fmtMetric(m?.leads ?? 0, 0, '')} />
          <KpiCard label="⌀ Kosten / Lead" value={fmtMetric(m?.costPerLead, 2, ' €')} />
        </Stack>
      </Paper>
    );
  };

  return (
    <Box m="20px">
      <Box
        justifyContent="space-between"
        alignItems="center"
        display="flex"
        flexWrap="wrap"
        gap={2}
      >
        <Header
          title="ROI A/B-Tests Auswertung"
          subtitle="Lead Distribution Test je Asset-Bucket: GEVESTOR vs INVESTOR"
        />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="network-select-label">Netzwerk</InputLabel>
            <Select
              labelId="network-select-label"
              id="network-select"
              multiple
              value={getSelectedNetworks()}
              onChange={handleNetworkChange}
              input={<OutlinedInput id="select-multiple-chip" label="Netzwerk" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Array.isArray(networks) &&
                networks.map((network) => (
                  <MenuItem key={network.name} value={network.name}>
                    {network.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="theme-select-label">Thema</InputLabel>
            <Select
              labelId="theme-select-label"
              id="theme-select"
              multiple
              value={getSelectedThemes()}
              onChange={handleThemeChange}
              input={<OutlinedInput id="select-multiple-theme-chip" label="Thema" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Array.isArray(themes) &&
                themes.map((theme) => (
                  <MenuItem key={theme.name} value={theme.name}>
                    {theme.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading && !catalog && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Lade Testdaten…
        </Alert>
      )}

      {catalog && catalog.buckets.length === 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          Keine LDBT-Testdaten im gewählten Zeitraum / Filter.
        </Alert>
      )}

      {selectedBucketObj && (
        <>
          {/* --- Test + regime selectors --- */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mt: 3, mb: 2 }}
          >
            <FormControl sx={{ minWidth: 260 }}>
              <InputLabel id="bucket-select-label">Test (Asset-Bucket)</InputLabel>
              <Select
                labelId="bucket-select-label"
                label="Test (Asset-Bucket)"
                value={selectedBucket}
                onChange={(e) => setSelectedBucket(String(e.target.value))}
              >
                {catalog!.buckets.map((b) => (
                  <MenuItem key={b.bucket} value={b.bucket}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: b.color,
                        }}
                      />
                      <span>
                        {b.name} ({b.bucket})
                      </span>
                      {catalog!.mostRecentChangeBucket === b.bucket && (
                        <Chip size="small" color="primary" label="zuletzt geändert" />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedBucketObj.hasSplit && selectedBucketObj.regimes.length > 1 && (
              <FormControl sx={{ minWidth: 320 }}>
                <InputLabel id="regime-select-label">Split-Regime</InputLabel>
                <Select
                  labelId="regime-select-label"
                  label="Split-Regime"
                  value={selectedRegimeIdx}
                  onChange={(e) => setSelectedRegimeIdx(Number(e.target.value))}
                >
                  {selectedBucketObj.regimes.map((r, idx) => (
                    <MenuItem key={`${r.gevPct}-${r.activeFrom}`} value={idx}>
                      {regimeLabel(r)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedBucketObj.hasSplit && selectedRegime && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" color="textSecondary">
                  {selectedRegime.isCurrent ? 'Neuestes Regime mit Daten' : 'Abgelöstes Regime'} ·
                  zuletzt aktiv {fmtDateDE(selectedRegime.lastSeen)} ·{' '}
                  {formatNumber(selectedRegime.leads, 0)} Leads (180-Tage-Fenster)
                </Typography>
                {selectedRegime.isCurrent && (
                  <Tooltip title="Basiert auf gebuchten Leads (Buchungs-/Mirror-Lag ~1–3 Tage). Eine erst heute geänderte Split-Konfiguration erscheint hier nicht sofort, sondern sobald die ersten Leads mit dem neuen Suffix eingelaufen sind — dies ist das neueste Regime mit Daten, nicht zwingend die aktuell live geschaltete Konfiguration.">
                    <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                  </Tooltip>
                )}
              </Stack>
            )}
          </Stack>

          {/* --- Passive contamination note --- */}
          {contaminated && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Hinweis Datenqualität (18.05.–23.06.2026):</strong> Im gewählten Zeitraum war
              der LDBT-AC-Cross-Sell ausgefallen. Bestehende GEVESTOR-Kunden, die normalerweise als
              „Already Customer“ zu INVESTOR cross-verkauft werden, fielen in die %-Split-Arme — das
              <strong> überzeichnet den ROI des INVESTOR-Arms</strong>. Für eine saubere Bewertung
              ein Fenster bis spätestens 17.05.2026 wählen. Die Daten werden bewusst nicht
              automatisch angepasst.
            </Alert>
          )}

          {selectedRegime?.partialRegime && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Neues Regime — ein Arm hat noch keine Daten (Buchungs-/Mirror-Lag, 1–3 Tage).
            </Alert>
          )}
          {!selectedRegime?.partialRegime && tinySample && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Kleine Stichprobe ({formatNumber(selectedRegime?.leads || 0, 0)} Leads) — ROI ist hier
              statistisch verrauscht; Gewinner-Markierung ausgeblendet.
            </Alert>
          )}
          {noWindowData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Keine Leads für dieses Regime im gewählten Zeitraum.
            </Alert>
          )}

          {/* --- GEV vs INV (split) OR single AC card --- */}
          {selectedBucketObj.hasSplit ? (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                {renderArmCard('GEV', gev, selectedRegime?.gevPct)}
                {renderArmCard('INV', inv, selectedRegime?.invPct)}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  GEVESTOR vs INVESTOR — {selectedBucketObj.name}
                </Typography>
                <Tooltip title="ROI 30 Tage und Conversion je Brand-Arm für das gewählte Regime und den gewählten Zeitraum. Beide Werte in %.">
                  <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                </Tooltip>
              </Stack>
              <Paper
                elevation={0}
                sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}
              >
                <Box sx={{ height: 280 }}>
                  <ResponsiveBar
                    data={chartData}
                    keys={['GEVESTOR', 'INVESTOR']}
                    indexBy="metric"
                    margin={{ top: 16, right: 120, bottom: 50, left: 70 }}
                    padding={0.3}
                    groupMode="grouped"
                    colors={[GEV_COLOR, INV_COLOR]}
                    axisLeft={{ legend: 'Wert (%)', legendPosition: 'middle', legendOffset: -55 }}
                    labelSkipHeight={12}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    valueFormat={(v) => `${Number(v).toLocaleString('de-DE')}%`}
                    legends={[
                      {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        translateX: 110,
                        itemWidth: 90,
                        itemHeight: 18,
                        symbolSize: 12,
                      },
                    ]}
                  />
                </Box>
              </Paper>
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Already Customer:</strong> deterministischer Cross-Sell zum jeweils anderen
                Brand — kein GEV/INV-Split. Erholt sich seit 23.06.2026 von der Mai–Juni-Störung
                (LDBT-AC-Ausfall).
              </Alert>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                <KpiCard label="Leads" value={fmtMetric(ac?.leads ?? 0, 0, '')} />
                <KpiCard label="ROI 30 Tage" value={fmtMetric(ac?.roi30, 1, ' %')} />
                <KpiCard label="Conversion" value={fmtMetric(ac?.conversion, 2, ' %')} />
                <KpiCard label="⌀ Kosten / Lead" value={fmtMetric(ac?.costPerLead, 2, ' €')} />
              </Stack>
            </>
          )}

          {/* --- Technical detail (collapsed) --- */}
          <Accordion sx={{ mb: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={600}>
                📋 Detail-Tabelle ({tableScopeLabel})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RoiTestsTable aggregation="detailed" testGroupFilter={selectedTestGroups} />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={600}>
                📋 Pro Newsletter ({tableScopeLabel})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RoiTestsPerNewsletterTable
                aggregation="detailed"
                testGroupFilter={selectedTestGroups}
              />
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
};

export default RoiTestsPage;
